import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaTimes, FaFlask } from 'react-icons/fa';
import {
  listKnowledgeEntries,
  saveKnowledgeEntry,
  setKnowledgeActive,
  testKnowledgeMatch,
} from '../../services/emailMonitorService';

// Manager for the FAQ / common-issues library that grounds auto-drafted replies.
// Small collection (tens of entries): one fetch lists everything, filters run in memory.

const INTENTS = [
  'technical_or_data_issue',
  'billing_or_rcti_query',
  'job_or_operational',
  'account_or_access',
  'general_inquiry',
];

const KIND_META = {
  faq: { label: 'FAQ', className: 'bg-blue-100 text-blue-800' },
  issue: { label: 'Issue', className: 'bg-red-100 text-red-800' },
};

const AUDIENCE_META = {
  franchisee: { label: 'Franchisees', className: 'bg-indigo-100 text-indigo-800' },
  supplier: { label: 'Suppliers', className: 'bg-purple-100 text-purple-800' },
  both: { label: 'Both', className: 'bg-green-100 text-green-800' },
};

const emptyForm = () => ({
  title: '', kind: 'faq', audience: 'both',
  problem: '', solution: '', intentTags: [], keywords: '', isActive: true,
});

const EntryModal = ({ entry, onClose, onSaved }) => {
  const [form, setForm] = useState(() => entry
    ? { ...emptyForm(), ...entry, keywords: (entry.keywords || []).join(', ') }
    : emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const patch = (changes) => setForm((prev) => ({ ...prev, ...changes }));
  const toggleIntent = (intent) => patch({
    intentTags: form.intentTags.includes(intent)
      ? form.intentTags.filter((t) => t !== intent)
      : [...form.intentTags, intent],
  });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveKnowledgeEntry(
        { ...form, keywords: form.keywords.split(',').map((k) => k.trim()).filter(Boolean) },
        entry?.id || null,
      );
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Could not save the entry.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full text-sm border border-gray-300 rounded px-3 py-2 focus:ring-jims-cyan focus:border-jims-cyan';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{entry ? 'Edit entry' : 'New entry'}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700">
            <FaTimes />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ke-title">Title</label>
            <input id="ke-title" value={form.title} disabled={saving} onChange={(e) => patch({ title: e.target.value })} placeholder="e.g. How to get a copy of an RCTI" className={inputClass} />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ke-kind">Kind</label>
              <select id="ke-kind" value={form.kind} disabled={saving} onChange={(e) => patch({ kind: e.target.value })} className={inputClass}>
                <option value="faq">FAQ</option>
                <option value="issue">Common issue</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ke-audience">Who may see this in a reply</label>
              <select id="ke-audience" value={form.audience} disabled={saving} onChange={(e) => patch({ audience: e.target.value })} className={inputClass}>
                <option value="both">Franchisees and suppliers</option>
                <option value="franchisee">Franchisees only</option>
                <option value="supplier">Suppliers only</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ke-problem">Problem (what the sender describes)</label>
            <textarea id="ke-problem" value={form.problem} disabled={saving} onChange={(e) => patch({ problem: e.target.value })} rows={3} className={inputClass} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ke-solution">Solution (what the draft may say — nothing else)</label>
            <textarea id="ke-solution" value={form.solution} disabled={saving} onChange={(e) => patch({ solution: e.target.value })} rows={4} className={inputClass} />
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">Email topics this answers</span>
            <div className="flex flex-wrap gap-2">
              {INTENTS.map((intent) => (
                <label key={intent} className={`px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer ${form.intentTags.includes(intent) ? 'bg-jims-cyan text-white border-jims-cyan' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                  <input type="checkbox" checked={form.intentTags.includes(intent)} disabled={saving} onChange={() => toggleIntent(intent)} className="sr-only" />
                  {intent}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ke-keywords">Keywords (comma separated, for matching)</label>
            <input id="ke-keywords" value={form.keywords} disabled={saving} onChange={(e) => patch({ keywords: e.target.value })} placeholder="rcti, copy, resend" className={inputClass} />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!form.isActive} disabled={saving} onChange={(e) => patch({ isActive: e.target.checked })} />
            Active (inactive entries are never used in drafts)
          </label>

          {error && <p className="text-sm text-red-700">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving || !form.title.trim()} className="px-4 py-2 text-sm font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark disabled:opacity-50">
            {saving ? 'Saving…' : 'Save entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TestMatchPanel = () => {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [senderGroup, setSenderGroup] = useState('franchisee');
  const [intent, setIntent] = useState('general_inquiry');
  const [result, setResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setTesting(true);
    setError(null);
    setResult(null);
    try {
      setResult(await testKnowledgeMatch({ subject, body, senderGroup, intent }));
    } catch (e) {
      setError(e.message || 'Could not test the match.');
    } finally {
      setTesting(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 text-sm text-jims-cyan hover:text-jims-cyan-dark font-medium">
        <FaFlask /> Test what an email would match
      </button>
    );
  }

  const inputClass = 'w-full text-sm border border-gray-300 rounded px-3 py-2 focus:ring-jims-cyan focus:border-jims-cyan';

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">Test match (read-only, costs one AI call)</h4>
        <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700" aria-label="Close test panel">
          <FaTimes />
        </button>
      </div>
      <div className="flex gap-3">
        <select value={senderGroup} onChange={(e) => setSenderGroup(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1.5" aria-label="Test sender">
          <option value="franchisee">From a franchisee</option>
          <option value="supplier">From a supplier</option>
        </select>
        <select value={intent} onChange={(e) => setIntent(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1.5" aria-label="Test topic">
          {INTENTS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className={inputClass} />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Paste the email text…" className={inputClass} />
      <div>
        <button type="button" onClick={run} disabled={testing || (!subject.trim() && !body.trim())} className="px-4 py-2 text-sm font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark disabled:opacity-50">
          {testing ? 'Matching…' : 'Test match'}
        </button>
      </div>
      {error && <p className="text-sm text-red-700">{error}</p>}
      {result && (
        <p className="text-sm text-gray-700">
          {result.entry
            ? <>Best match: <b>{result.entry.title}</b> (certainty {Number(result.confidence || 0).toFixed(2)}; drafts need {Number(result.threshold ?? 0.7).toFixed(2)}).</>
            : 'No entry matches — a generic holding reply would be drafted.'}
        </p>
      )}
    </div>
  );
};

const KnowledgeTab = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);
  const [search, setSearch] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listKnowledgeEntries();
      setEntries(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message || 'Failed to load knowledge entries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (entry) => {
    try {
      await setKnowledgeActive(entry.id, !entry.isActive);
      setBanner({ type: 'success', text: entry.isActive ? 'Entry deactivated — it will no longer ground drafts.' : 'Entry activated.' });
      await load();
    } catch (e) {
      setBanner({ type: 'error', text: e.message || 'Could not update the entry.' });
    }
  };

  const q = search.trim().toLowerCase();
  const visible = entries
    .filter((e) => showInactive || e.isActive)
    .filter((e) => !audienceFilter || e.audience === audienceFilter)
    .filter((e) => !kindFilter || e.kind === kindFilter)
    .filter((e) => !q || [e.title, e.problem, e.solution, (e.keywords || []).join(' ')].join(' ').toLowerCase().includes(q));

  const selectClass = 'text-sm border border-gray-300 rounded px-2 py-1.5 focus:ring-jims-cyan focus:border-jims-cyan';

  return (
    <div>
      {banner && (
        <div className={`mb-4 px-4 py-3 rounded border flex justify-between items-start ${
          banner.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <span>{banner.text}</span>
          <button onClick={() => setBanner(null)} className="ml-4 text-sm opacity-70 hover:opacity-100">Dismiss</button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Reply knowledge</h2>
              <p className="text-xs text-gray-500 mt-0.5">FAQs and common issues the monitor may answer from. Audience is enforced: franchisee-only entries never reach suppliers and vice versa.</p>
            </div>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark shrink-0"
            >
              <FaPlus className="text-xs" /> New entry
            </button>
          </div>

          <div className="flex gap-2 flex-wrap">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, problem, solution…"
              aria-label="Search knowledge"
              className={`${selectClass} flex-1 min-w-0`}
            />
            <select value={audienceFilter} onChange={(e) => setAudienceFilter(e.target.value)} className={selectClass} aria-label="Audience filter">
              <option value="">All audiences</option>
              <option value="franchisee">Franchisees</option>
              <option value="supplier">Suppliers</option>
              <option value="both">Both</option>
            </select>
            <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className={selectClass} aria-label="Kind filter">
              <option value="">FAQs + issues</option>
              <option value="faq">FAQs</option>
              <option value="issue">Issues</option>
            </select>
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
          </div>

          <TestMatchPanel />
        </div>

        <div className="overflow-y-auto max-h-[calc(100vh-20rem)] min-h-[20rem]">
          {loading ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">Loading&hellip;</p>
          ) : error ? (
            <p className="px-4 py-8 text-center text-sm text-red-700">{error}</p>
          ) : visible.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-gray-500">No entries match. Add the first FAQ or common issue above.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {visible.map((entry) => {
                const kind = KIND_META[entry.kind] || KIND_META.faq;
                const audience = AUDIENCE_META[entry.audience] || AUDIENCE_META.both;
                return (
                  <li key={entry.id} className={`px-4 py-3 ${entry.isActive ? '' : 'opacity-60'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{entry.title}</p>
                        <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{entry.problem}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${kind.className}`}>{kind.label}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${audience.className}`}>{audience.label}</span>
                          {!entry.isActive && (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">Inactive</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button type="button" onClick={() => setEditing(entry)} className="text-sm text-jims-cyan hover:text-jims-cyan-dark font-medium">
                          Edit
                        </button>
                        <button type="button" onClick={() => toggleActive(entry)} className="text-sm text-gray-500 hover:text-gray-800 font-medium">
                          {entry.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {(creating || editing) && (
        <EntryModal
          entry={editing || null}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={async () => { setBanner({ type: 'success', text: 'Entry saved.' }); await load(); }}
        />
      )}
    </div>
  );
};

export default KnowledgeTab;
